/**
 * Incident Editor Dialog - Resolution and Closure Tab (PROJ-007)
 * 
 * CONSOLIDATED TAB: Unifies all resolution and closure information
 * Previously fragmented across 3 tabs:
 * - ResolutionTab: resolutionType, resolution
 * - DatesTab: resolvedAt, closedAt
 * - PeopleTab: resolvedByUserId, closedByUserId
 * 
 * This consolidation eliminates the need to navigate 3 different tabs
 * to complete resolution/closure workflow.
 */

import { Alert, AlertDescription } from '@/components/ui/alert';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { Separator } from '@/components/ui/separator';
import { CheckCircle2, X } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import type { IncidentFormData } from './types';

interface ResolutionAndClosureTabProps {
  form: UseFormReturn<IncidentFormData>;
  isViewMode?: boolean;
}

export function ResolutionAndClosureTab({ form, isViewMode = false }: ResolutionAndClosureTabProps) {
  const state = form.watch('state');
  const isResolved = state === 'resolved' || state === 'closed';

  return (
    <div className="space-y-6">
      <Alert>
        <CheckCircle2 className="h-4 w-4" />
        <AlertDescription>
          <strong>Tab consolidado:</strong> Toda la información de resolución y cierre en un solo lugar.
          No necesitas navegar entre múltiples pestañas.
        </AlertDescription>
      </Alert>

      {!isResolved && (
        <Alert variant="destructive">
          <X className="h-4 w-4" />
          <AlertDescription>
            Esta pestaña se habilita cuando el estado es "Resuelto" o "Cerrado"
          </AlertDescription>
        </Alert>
      )}

      {/* Resolution Type & Description Section */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground">Información de Resolución</h3>

        <FormField
          control={form.control}
          name="resolutionType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tipo de Resolución</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value ?? ''}
                disabled={!isResolved || isViewMode}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar tipo de resolución" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="z-[var(--z-popover-in-dragging-dialog)]">
                  <SelectItem value="fixed">Corregido - Problema resuelto permanentemente</SelectItem>
                  <SelectItem value="workaround">Workaround - Solución temporal implementada</SelectItem>
                  <SelectItem value="duplicate">Duplicado - Ya existe otro incidente</SelectItem>
                  <SelectItem value="cancelled">Cancelado - No procede resolver</SelectItem>
                  <SelectItem value="not_resolved">No Resuelto - No se pudo resolver</SelectItem>
                  <SelectItem value="by_design">Por Diseño - Comportamiento esperado</SelectItem>
                  <SelectItem value="external_fix">Corrección Externa - Resuelto por terceros</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>
                Clasificación del tipo de resolución aplicada
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="resolution"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Descripción de la Resolución</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Describe detalladamente cómo se resolvió el incidente, qué acciones se tomaron, qué cambios se implementaron, etc..."
                  rows={8}
                  className="resize-none"
                  disabled={!isResolved || isViewMode}
                />
              </FormControl>
              <FormDescription>
                Narrativa completa de la resolución (obligatorio al resolver)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <Separator />

      {/* Resolution Date & Person Section */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground">Fecha y Responsable de Resolución</h3>

        <FormField
          control={form.control}
          name="resolvedAt"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <DateTimePicker
                  value={field.value || null}
                  onChange={field.onChange}
                  label="Fecha de Resolución"
                  placeholder="Cuándo se resolvió el incidente"
                  id="resolvedAt"
                  disabled={!isResolved || isViewMode}
                />
              </FormControl>
              <FormDescription>
                Cuándo se resolvió el incidente (solución implementada)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="resolvedByUserId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Resuelto Por (UUID)</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  disabled={!isResolved || isViewMode}
                />
              </FormControl>
              <FormDescription>
                Persona que resolvió el incidente
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <Separator />

      {/* Closure Date & Person Section */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground">Fecha y Responsable de Cierre</h3>

        <FormField
          control={form.control}
          name="closedAt"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <DateTimePicker
                  value={field.value || null}
                  onChange={field.onChange}
                  label="Fecha de Cierre"
                  placeholder="Cuándo se cerró el incidente"
                  id="closedAt"
                  disabled={!isResolved || isViewMode}
                />
              </FormControl>
              <FormDescription>
                Cuándo se cerró formalmente el incidente (después de resolución)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="closedByUserId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Cerrado Por (UUID)</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  disabled={!isResolved || isViewMode}
                />
              </FormControl>
              <FormDescription>
                Persona que cerró formalmente el incidente
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {isResolved && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>
            <strong>✅ Mejora de UX:</strong> Todos los campos relacionados con resolución y cierre 
            ahora están en un solo lugar. Ya no necesitas navegar entre "Resolución", "Fechas" y "Personas".
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
